import { createFileRoute, Link } from "@tanstack/react-router";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";
import { PageHero, ServicePageBody } from "@/components/site/PageShell";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { BUSINESS } from "@/components/site/constants";
import { ResponsivePicture } from "@/components/site/images";
import {
  CRYSTAL_RIVER_BLUE_SHINGLE,
  LECANTO_BLACK_SHINGLE,
  BEVERLY_HILLS_SHINGLE_TEAR_OFF,
  BEVERLY_HILLS_SHINGLE_UNDERLAYMENT,
  BEVERLY_HILLS_SHINGLE_IN_PROGRESS,
} from "@/components/site/project-images";
import { Phone, CalendarCheck } from "lucide-react";

const TITLE = "Shingle Roofing in Citrus County, FL | Destination Roofing";
const DESCRIPTION =
  "Shingle roofing in Citrus County, FL, including Atlas roof replacement and repair. Free owner-performed inspection and strong workmanship warranties.";

const FAQS: { q: string; a: string }[] = [
  {
    q: "Which shingle brand does Destination Roofing use?",
    a: "Destination Roofing primarily installs Atlas shingles. Pinnacle Pristine is the company's most commonly installed architectural shingle, while Pinnacle Sun may be offered as an upgraded option.",
  },
  {
    q: "Why does Destination Roofing prefer Atlas shingles?",
    a: "Deston considers Atlas shingles robust enough for demanding Florida conditions and values the complete-system warranty options available when qualifying Atlas components are installed.",
  },
  {
    q: "How many nails are installed in each shingle?",
    a: "Destination Roofing generally installs six 1½-inch roofing nails per field shingle and four nails per ridge-cap shingle as part of the company's high-wind-focused installation standard. Exact fastening must follow the approved roof system and manufacturer requirements.",
  },
  {
    q: "What is installed beneath the shingles?",
    a: "Destination Roofing commonly uses Atlas Summit 60 synthetic underlayment as part of qualifying Atlas systems. The roof deck is inspected before the new system is installed.",
  },
  {
    q: "What is a California-cut valley?",
    a: "A California-cut valley is a shingle-valley installation method that creates a defined water path while integrating shingles from adjoining roof slopes. Correct layout, underlayment, and fastener placement are important.",
  },
  {
    q: "Does a roof leak mean the shingles need replacement?",
    a: "No. An isolated damaged shingle, pipe boot, flashing detail, valley, or penetration may be repairable when the surrounding roof remains serviceable.",
  },
  {
    q: "What does granule loss look like?",
    a: "Aging shingles may appear faded, dark where asphalt is exposed, or shiny where fiberglass has become visible. Widespread granule loss can indicate that the roof is nearing the point where replacement should be considered.",
  },
  {
    q: "Can new shingles match an older roof?",
    a: "Destination Roofing attempts to use the closest compatible material available, but exact color matching may not be possible because shingles fade and older products may be discontinued.",
  },
  {
    q: "Should skylights be replaced during a reroof?",
    a: "Destination Roofing commonly recommends replacing aging skylights during a full reroof because an older skylight can become a future leak point. The correct choice depends on the skylight's age and condition.",
  },
  {
    q: "What happens when rotten plywood is found?",
    a: "Destination Roofing photographs damaged decking, explains the condition, and replaces the affected material according to the approved contract. Two sheets are generally included in a full-roof proposal.",
  },
  {
    q: "What workmanship warranty is included?",
    a: "Standard full-roof installations generally receive a 15-year Destination Roofing workmanship warranty. A qualifying Atlas Signature Select system may receive a 20-year workmanship warranty. Exact coverage is controlled by the written contract and warranty.",
  },
  {
    q: "Are roof inspections free?",
    a: "Yes. Destination Roofing provides free roof inspections and replacement estimates.",
  },
  {
    q: "Who performs the roof inspection?",
    a: "Owner and licensed roofing contractor Deston Judy generally performs shingle-roof inspections personally and gets onto the roof when weather, pitch, and safety conditions allow.",
  },
  {
    q: "How quickly can Destination Roofing inspect my roof?",
    a: "Destination Roofing aims to contact new leads promptly, often within about one hour. Same-day or next-day appointments are commonly available depending on schedule, weather, roof safety, and location.",
  },
];

const REVIEWS = [
  {
    name: "Back Water Services",
    text: "We just had our roof replaced by Destination Roofing. They did an AMAZING job! Their communication throughout the project was excellent.",
  },
  {
    name: "Cyndie Burke",
    text: "Destination Roofing gave us a fair quote and completed the job in one day. The team he had doing our roof were efficient and professional.",
  },
  {
    name: "Kathy Chapman",
    text: "Destin and his crew did an amazing job on the roof where I work. They made every effort to ensure we were aware of timing, clean up process and followed up immediately. Would highly recommend them.",
  },
];

export const Route = createFileRoute("/shingle-roofing")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: canonical("/shingle-roofing") },
      { property: "og:type", content: "website" },
      { property: "og:image", content: CRYSTAL_RIVER_BLUE_SHINGLE.fallback },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: CRYSTAL_RIVER_BLUE_SHINGLE.fallback },
    ],
    links: [{ rel: "canonical", href: canonical("/shingle-roofing") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify([
          breadcrumbListJsonLd([
            { label: "Services", href: "/services" },
            { label: "Shingle Roofing", href: "/shingle-roofing" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Shingle Roofing",
            serviceType: "Asphalt Shingle Roofing",
            description:
              "Architectural asphalt shingle roof installation, replacement, and repair using Atlas roofing systems in Citrus County, FL.",
            url: canonical("/shingle-roofing"),
            provider: { "@id": ROOFING_CONTRACTOR_ID },
            areaServed: { "@type": "AdministrativeArea", name: "Citrus County, Florida" },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]),
      },
    ],
  }),
  component: Page,
});

function TrustRow() {
  const items = [
    "Free roof inspections",
    "Owner-performed inspections",
    "Atlas shingle systems",
    "Six nails per field shingle",
    "Strong written workmanship warranties",
    "Licensed and insured",
    `License # ${BUSINESS.licenseNumber}`,
  ];
  return (
    <div className="not-prose bg-muted/50 border border-border rounded-lg p-4 my-6">
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {items.map((t) => (
          <li key={t} className="flex items-start gap-2">
            <span className="mt-1 size-1.5 rounded-full bg-cta shrink-0" aria-hidden />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function WhyCards() {
  const cards = [
    {
      h: "Owner Involvement",
      p: "Deston generally performs the inspection, explains the estimate, checks the finished roof, and remains available throughout the project.",
    },
    {
      h: "Honest Recommendations",
      p: "Destination Roofing recommends repair when a relatively serviceable roof has a clear, isolated failure instead of automatically pushing full replacement.",
    },
    {
      h: "Complete Roofing System",
      p: "The company evaluates underlayment, starter shingles, field shingles, ridge caps, flashing, valleys, penetrations, ventilation, and roof decking as parts of one system.",
    },
    {
      h: "Competitive Pricing",
      p: "Lean overhead and direct owner involvement help Destination Roofing provide competitive pricing without removing ordinary roofing components from the proposal.",
    },
  ];
  return (
    <div className="not-prose grid sm:grid-cols-2 gap-4 my-6">
      {cards.map((c) => (
        <div key={c.h} className="border border-border rounded-lg p-5 bg-card">
          <h3 className="font-display font-bold text-lg mb-2">{c.h}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{c.p}</p>
        </div>
      ))}
    </div>
  );
}

function RepairReplaceColumns() {
  const cols = [
    {
      h: "Repair may make sense when:",
      items: [
        "Damage is isolated",
        "The roof is relatively serviceable",
        "The leak source can be identified",
        "Matching or compatible materials are available",
        "Surrounding shingles remain secure",
        "Decking remains structurally sound",
        "A proper repair can reasonably restore the area",
      ],
      accent: true,
    },
    {
      h: "Replacement may be more practical when:",
      items: [
        "Granule loss is widespread",
        "Fiberglass is exposed across multiple areas",
        "Leaks repeatedly return",
        "Multiple roof slopes are deteriorating",
        "Decking damage is extensive",
        "Previous repairs continue failing",
        "The roofing system is no longer dependable",
        "Repair cost would consume a substantial portion of replacement cost",
      ],
      accent: false,
    },
  ];
  return (
    <div className="not-prose grid md:grid-cols-2 gap-4 my-6">
      {cols.map((c) => (
        <div
          key={c.h}
          className={`rounded-lg p-5 bg-card ${c.accent ? "border-2 border-cta" : "border border-border"}`}
        >
          <h3 className="font-display font-bold text-base mb-3">{c.h}</h3>
          <ul className="space-y-1.5 text-sm">
            {c.items.map((i) => (
              <li key={i}>· {i}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function InProgressBadge() {
  return (
    <span className="absolute left-3 top-3 z-10 rounded bg-brand/90 px-2.5 py-1 text-xs font-display font-bold uppercase tracking-wider text-brand-foreground">
      Project In Progress
    </span>
  );
}

function Page() {
  return (
    <>
      <Breadcrumbs
        items={[
          { label: "Services", href: "/services" },
          { label: "Shingle Roofing", href: "/shingle-roofing" },
        ]}
      />
      <PageHero showRating
        eyebrow="Shingle Roofing"
        title="Shingle Roofing in Citrus County, FL"
        subtitle="Destination Roofing Inc. installs, replaces, and repairs architectural shingle roofs throughout Citrus County. We primarily work with Atlas roofing systems because Deston considers the shingles robust enough for demanding Florida conditions and values the manufacturer's complete-system warranty options. Owner and licensed roofing contractor Deston Judy generally performs the roof inspection personally and explains whether repair or full replacement appears most reasonable."
      />

      <ServicePageBody title="Shingle Roofing" service="Shingle Roofing">
        <TrustRow />

        <div className="not-prose flex flex-col sm:flex-row gap-3 my-6">
          <a href="#lead-form" className="btn-cta w-full sm:w-auto">
            <CalendarCheck className="size-5" /> Schedule My Free Shingle-Roof Inspection
          </a>
          <a
            href={BUSINESS.phoneHref}
            className="inline-flex items-center justify-center gap-2 border-2 border-brand text-brand font-display font-bold uppercase tracking-wider rounded-md px-6 py-3 w-full sm:w-auto hover:bg-brand hover:text-brand-foreground transition-colors"
          >
            <Phone className="size-5" /> Call {BUSINESS.phone}
          </a>
        </div>

        <h2>A More Personal Shingle-Roofing Experience</h2>
        <WhyCards />

        <h2>Why Architectural Shingles Remain a Popular Choice</h2>
        <p>
          Architectural (dimensional) shingles are the most common residential roof in Citrus County, and for
          understandable reasons.
        </p>
        <ul>
          <li>Lower initial cost than most <Link to="/metal-roofing">metal roofing</Link> systems</li>
          <li>A wide range of colors and appearances</li>
          <li>Suitable for many residential roof designs</li>
          <li>A familiar appearance in Citrus County neighborhoods</li>
          <li>May better fit HOA requirements than some metal systems</li>
          <li>Established manufacturer system-warranty options</li>
          <li>Practical repairability when damage is isolated</li>
          <li>A good balance between price, appearance, and performance</li>
        </ul>
        <p>
          Shingle roofing is selected by most Destination Roofing residential customers, primarily because of price and
          HOA requirements. If you are weighing systems side by side, see our{" "}
          <Link to="/roof-replacement">roof replacement</Link> page and our{" "}
          <Link to="/atlas-shingle-colors">Atlas shingle color options</Link>.
        </p>

        <h2>Atlas Shingle Roofing Systems</h2>
        <p>
          Destination Roofing primarily installs Atlas roofing systems. Deston prefers Atlas because he considers the
          shingles more robust than many lower-cost products and better suited to demanding Florida heat.
        </p>
        <p>
          No single shingle is the right answer for every home. Availability, color, roof design, homeowner preference,
          and current manufacturer requirements all influence which product is specified for a particular roof.
        </p>

        <h2>Atlas Pinnacle Pristine Shingles</h2>
        <p>Atlas Pinnacle Pristine is Destination Roofing's most commonly installed architectural shingle.</p>
        <ul>
          <li>Architectural, dimensional appearance</li>
          <li>Multiple color options</li>
          <li>Designed as part of an Atlas roofing system</li>
          <li>Scotchgard-related algae-resistance technology where it is included in the selected product</li>
          <li>Manufacturer warranty options when installed with qualifying system components</li>
        </ul>
        <p>
          Algae resistance, granule technology, and weathering performance reduce risk — they do not eliminate it. No
          shingle can be promised to never stain, fade, or sustain weather damage. Warranty terms depend on current
          official Atlas documents for the exact product and system installed.
        </p>

        <h2>Atlas Pinnacle Sun Upgrade Option</h2>
        <p>
          Pinnacle Sun may be offered as an upgraded Atlas shingle option when it is appropriate for the home, budget,
          desired color, availability, and roofing system.
        </p>
        <p>
          Depending on the exact product selected, it may use reflective or cool-roof granule technology. Destination
          Roofing does not promise a specific energy-savings percentage, a specific utility-bill reduction, a specific
          attic-temperature reduction, or eligibility for any rebate or insurance discount. Color and product
          availability can be reviewed on our <Link to="/atlas-shingle-colors">Atlas shingle colors</Link> page.
        </p>

        <h2>More Than Just the Visible Shingles</h2>
        <p>A qualifying Atlas Signature Select installation may include:</p>
        <ul>
          <li>Atlas Summit 60 synthetic underlayment</li>
          <li>Atlas Pro-Cut starter shingles</li>
          <li>Atlas Pinnacle field shingles</li>
          <li>Atlas Pro-Cut hip-and-ridge cap shingles</li>
        </ul>
        <h3>Underlayment</h3>
        <p>
          Underlayment provides a secondary water-shedding layer beneath the shingles. It is not the finished roof
          surface — it protects the deck while the roof is being built and adds a second line of defense afterward.
        </p>

        <figure className="my-6 rounded-xl overflow-hidden border border-border bg-card not-prose relative">
          <InProgressBadge />
          <ResponsivePicture
            image={BEVERLY_HILLS_SHINGLE_UNDERLAYMENT}
            alt="Synthetic roof underlayment installed during a shingle replacement in Beverly Hills, Florida"
            sizes="(min-width: 1024px) 60vw, 100vw"
            imgClassName="w-full h-auto"
          />
          <figcaption className="p-3 text-sm text-muted-foreground">
            Synthetic underlayment installed across a{" "}
            <Link to="/roofing-company-beverly-hills-fl">Beverly Hills</Link> roof before the new shingles are applied.
            The underlayment shown here is a secondary protective layer beneath the finished shingles, not the completed
            roofing surface.
          </figcaption>
        </figure>

        <h3>Starter shingles</h3>
        <p>Starter shingles provide the first sealed and fastened course at the roof edges.</p>
        <h3>Field shingles</h3>
        <p>Field shingles form the main visible roof surface.</p>
        <h3>Hip-and-ridge shingles</h3>
        <p>Hip-and-ridge shingles protect hips and ridges while providing a finished appearance.</p>
        <p>
          Exact manufacturer warranty eligibility depends on the selected products, installation, registration, and
          current Atlas warranty requirements. A Signature Select system is not automatically all-inclusive.
        </p>

        <h2>How Destination Roofing Fastens Shingles</h2>
        <ul>
          <li>1½-inch roofing nails</li>
          <li>Coil-gun fastening for ordinary shingle installation</li>
          <li>Six nails per field shingle</li>
          <li>Four nails per ridge-cap shingle as Destination Roofing's high-wind standard</li>
          <li>Hand fastening only when required for certain manufactured ventilation or roof-detail components</li>
          <li>Fasteners placed according to the approved shingle and roof-system requirements</li>
        </ul>
        <p>
          Correct nail placement matters as much as nail quantity. Nails that are driven too high, too low, overdriven,
          underdriven, or too close to vulnerable roof details can contribute to failures.
        </p>

        <h2>Roof Details That Help Prevent Leaks</h2>
        <h3>Valleys</h3>
        <p>Destination Roofing commonly installs California-cut shingle valleys.</p>
        <ul>
          <li>Courses are laid out carefully.</li>
          <li>Fasteners are kept away from vulnerable valley areas.</li>
          <li>Water must have a clear drainage path.</li>
          <li>Valley details must integrate with the underlayment and surrounding shingles.</li>
        </ul>
        <h3>Layout lines</h3>
        <p>Snapped chalk lines help keep shingle courses straight and consistent across the roof.</p>
        <h3>Flashing</h3>
        <p>Existing flashing is exposed and inspected during tear-off.</p>
        <ul>
          <li>Sound flashing may remain when it is still structurally serviceable.</li>
          <li>Severely rusted, perforated, or failed flashing is replaced.</li>
          <li>Sidewall and headwall details receive appropriate flashing and sealant.</li>
          <li>
            Roofing cement — sometimes called "step bull" on the jobsite — may be used where it is required as part of
            proper sidewall or penetration detailing.
          </li>
        </ul>
        <h3>Pipe boots</h3>
        <p>Destination Roofing commonly installs lead pipe boots for plumbing penetrations.</p>
        <h3>Electrical risers</h3>
        <p>Electrical risers may receive neoprene zipper boots.</p>
        <h3>Chimneys</h3>
        <p>
          Larger chimneys may require a cricket to divert water, depending on the chimney width, roof design, and
          applicable requirements.
        </p>
        <h3>Skylights</h3>
        <p>
          Destination Roofing commonly recommends replacing aging skylights during a full reroof, because building a new
          roof around an old skylight may create a future leak risk. Not every skylight has to be replaced — age and
          condition decide.
        </p>

        <h2>Roof Ventilation and Soffit Intake</h2>
        <p>Roof exhaust and soffit intake should function together as one airflow system.</p>
        <ul>
          <li>Aluminum ridge vents with appropriate fasteners</li>
          <li>Lower-profile Omni-style ridge vents</li>
          <li>Off-ridge vents when insufficient ridge length exists</li>
          <li>Existing ventilation retained when it remains appropriate and serviceable</li>
        </ul>
        <p>
          Ventilation selection depends on attic design, available ridge length, soffit intake, roof layout, existing
          airflow, applicable requirements, and homeowner preference. Ventilation is not a guaranteed utility-bill
          reduction. Related: <Link to="/soffit">soffit and fascia</Link> and{" "}
          <Link to="/roof-inspection">free roof inspection</Link>.
        </p>

        <h2>Signs Your Shingle Roof May Need Attention</h2>
        <p>
          One of the earliest visible signs of an aging shingle roof is often significant granule loss. The roof may
          appear faded, dark where asphalt is showing, or shiny where fiberglass has become exposed.
        </p>
        <ul>
          <li>Significant granule loss</li>
          <li>Shingles appearing dark or black where asphalt is exposed</li>
          <li>A shiny or glittering appearance where fiberglass is visible</li>
          <li>Missing shingles</li>
          <li>Lifted or creased shingles</li>
          <li>Cracked or curling shingles</li>
          <li>Loose or missing ridge caps</li>
          <li>Repeated leaks</li>
          <li>Ceiling stains</li>
          <li>Failed pipe boots</li>
          <li>Flashing deterioration</li>
          <li>Soft or damaged roof decking</li>
          <li>Widespread storm damage</li>
        </ul>
        <p>
          A single damaged shingle does not automatically mean the entire roof requires replacement. The full condition
          must be inspected. See <Link to="/roof-repair">roof repair</Link> and{" "}
          <Link to="/roof-replacement">roof replacement</Link>.
        </p>

        <h2>Should a Shingle Roof Be Repaired or Replaced?</h2>
        <RepairReplaceColumns />
        <p>
          Destination Roofing has talked homeowners out of full replacement when a proper repair was the more reasonable
          choice. If you are unsure which applies to your roof, start with a free{" "}
          <Link to="/roof-inspection">roof inspection</Link>.
        </p>

        <h2>What Happens When Damaged Roof Decking Is Found?</h2>
        <p>The roof deck cannot be fully evaluated until the existing shingles and underlayment are removed.</p>
        <p>Destination Roofing checks for:</p>
        <ul>
          <li>Water staining</li>
          <li>Softness</li>
          <li>Swelling</li>
          <li>Delamination</li>
          <li>Rot</li>
          <li>Loss of structural strength</li>
          <li>Damage near penetrations and leak areas</li>
          <li>Failed sheet or plank connections</li>
        </ul>
        <p>
          Water staining is often the first visible warning that decking may have lost strength or begun deteriorating.
          Destination Roofing also checks the physical condition before determining the proper repair, rather than
          removing every stained board automatically.
        </p>
        <ul>
          <li>Damaged decking is photographed before removal.</li>
          <li>Homeowners are shown why replacement is necessary.</li>
          <li>Two sheets of replacement decking are generally included in a full reroof.</li>
          <li>Additional decking follows the written contract.</li>
          <li>½-inch OSB is common and is the minimum sheet thickness Destination Roofing generally uses.</li>
          <li>Plywood may be provided when the homeowner prefers it, with price adjusted accordingly.</li>
          <li>Older homes may have sturdy 1x8 plank decking.</li>
        </ul>

        <h2>What Affects Shingle-Roofing Cost in Citrus County?</h2>
        <ul>
          <li>Total roof surface area</li>
          <li>Roof pitch</li>
          <li>One-story versus two-story access</li>
          <li>Number of valleys, hips, ridges, and roof sections</li>
          <li>Existing roofing layers</li>
          <li>Tear-off requirements</li>
          <li>Shingle product and upgrade choice</li>
          <li>Damaged decking</li>
          <li>Chimneys and crickets</li>
          <li>Skylights</li>
          <li>Electrical risers</li>
          <li>Ventilation changes</li>
          <li>Fascia, soffit, or wood repair</li>
          <li>Roof access</li>
          <li>Permit and disposal requirements</li>
        </ul>
        <p>
          Destination Roofing prices the entire roofing package rather than forcing the homeowner to calculate ordinary
          items such as drip edge, standard flashing, ventilation, permits, and disposal separately.
        </p>
        <p>See our <Link to="/roof-cost-citrus-county">2026 Citrus County roof-cost guide</Link> for architectural shingle and metal price ranges.</p>
        <ul>
          <li>Estimates are generally valid for 60 days.</li>
          <li>
            The normal payment structure is a $500 good-faith deposit with the balance due upon completion, subject to
            the signed contract.
          </li>
          <li>
            Material-price increases are not normally passed to the homeowner after a binding contract is signed during
            the estimate-validity period.
          </li>
        </ul>

        <h2>Our Shingle-Roofing Process</h2>
        <ol>
          <li>
            <strong>Initial contact.</strong> Liz or Deston receives the phone or website request and creates a work
            order in AccuLynx.
          </li>
          <li>
            <strong>Free inspection.</strong> Deston generally inspects the roof personally.
          </li>
          <li>
            <strong>Honest recommendation.</strong> The homeowner receives an explanation of whether repair or
            replacement appears most reasonable.
          </li>
          <li>
            <strong>Written estimate.</strong> The estimate is commonly prepared and reviewed at the property.
          </li>
          <li>
            <strong>Shingle and color selection.</strong> The homeowner selects the Atlas product, color, and any
            available upgrade.
          </li>
          <li>
            <strong>Permits, tear-off, and installation.</strong> The office handles permits and inspection scheduling.
            Existing roofing is removed, the deck is inspected, and the approved system is installed.
          </li>
          <li>
            <strong>Owner review and cleanup.</strong> Deston checks overall workmanship and cleanup. Final photographs
            are taken and a walkthrough is provided when the homeowner is available.
          </li>
        </ol>

        <figure className="my-6 rounded-xl overflow-hidden border border-border bg-card not-prose relative">
          <InProgressBadge />
          <ResponsivePicture
            image={BEVERLY_HILLS_SHINGLE_TEAR_OFF}
            alt="Destination Roofing crew tearing off an existing shingle roof in Beverly Hills, Florida"
            sizes="(min-width: 1024px) 60vw, 100vw"
            imgClassName="w-full h-auto"
          />
          <figcaption className="p-3 text-sm text-muted-foreground">
            Destination Roofing crew removing an existing shingle roof during a current replacement project in{" "}
            <Link to="/roofing-company-beverly-hills-fl">Beverly Hills, Florida</Link>.{" "}
                <Link to="/projects/shingle-roof-replacement-beverly-hills-fl">View project</Link>
          </figcaption>
        </figure>

        <h2>Protecting Your Property During Shingle Replacement</h2>
        <ul>
          <li>Landscaping may be protected with tarps.</li>
          <li>Tarps collect tear-off debris.</li>
          <li>Plywood or tarps may be used to protect newer or high-quality driveways.</li>
          <li>Exterior air-conditioning equipment may be shielded from falling debris.</li>
          <li>Pool areas affected by debris are cleaned.</li>
          <li>Roofing debris is collected and removed.</li>
          <li>Magnetic nail bars are run repeatedly around the property.</li>
          <li>Deston personally reviews workmanship and overall cleanup.</li>
        </ul>
        <p>
          A reroof may involve removing thousands of nails. Destination Roofing performs multiple magnetic sweeps
          because another fastener can still be found even after the area has already been checked several times.
        </p>

        <h2>Recent Shingle-Roofing Projects in Citrus County</h2>
        <div className="not-prose grid gap-6 md:grid-cols-3 my-6">
          <figure>
            <div className="overflow-hidden rounded-lg border border-border bg-muted">
              <ResponsivePicture
                image={CRYSTAL_RIVER_BLUE_SHINGLE}
                alt="Completed blue architectural shingle roof by Destination Roofing Inc. in Crystal River, Florida"
                sizes="(min-width: 768px) 33vw, 100vw"
                imgClassName="w-full h-56 object-cover"
              />
            </div>
            <figcaption className="mt-2 text-sm">
              <span className="block font-semibold">Blue Shingle Roof in Crystal River</span>
              <span className="block text-muted-foreground">
                Completed architectural shingle roofing project in{" "}
                <Link to="/roofing-company-crystal-river-fl">Crystal River, Florida</Link>.{" "}
                <Link to="/projects/blue-shingle-roof-crystal-river-fl">View project</Link>
              </span>
            </figcaption>
          </figure>

          <figure>
            <div className="overflow-hidden rounded-lg border border-border bg-muted">
              <ResponsivePicture
                image={LECANTO_BLACK_SHINGLE}
                alt="Completed black architectural shingle roof installed by Destination Roofing in Lecanto, Florida"
                sizes="(min-width: 768px) 33vw, 100vw"
                imgClassName="w-full h-56 object-cover"
              />
            </div>
            <figcaption className="mt-2 text-sm">
              <span className="block font-semibold">Black Shingle Roof in Lecanto</span>
              <span className="block text-muted-foreground">
                Completed black architectural shingle roof in{" "}
                <Link to="/roofing-company-lecanto-fl">Lecanto, Florida</Link>.
              </span>
            </figcaption>
          </figure>

          <figure>
            <div className="relative overflow-hidden rounded-lg border border-border bg-muted">
              <InProgressBadge />
              <ResponsivePicture
                image={BEVERLY_HILLS_SHINGLE_IN_PROGRESS}
                alt="Residential shingle roof replacement in progress in Beverly Hills, Florida"
                sizes="(min-width: 768px) 33vw, 100vw"
                imgClassName="w-full h-56 object-cover"
              />
            </div>
            <figcaption className="mt-2 text-sm">
              <span className="block font-semibold">Shingle Roof Replacement in Beverly Hills</span>
              <span className="block text-muted-foreground">
                Active tear-off and preparation during a residential shingle-roof replacement in{" "}
                <Link to="/roofing-company-beverly-hills-fl">Beverly Hills, Florida</Link>.
              </span>
            </figcaption>
          </figure>
        </div>
        <p>
          <Link to="/gallery">View More Roofing Projects</Link> or read a full{" "}
          <Link to="/projects">project case study</Link>.
        </p>

        <h2>Shingle-Roof Workmanship and Manufacturer Warranties</h2>
        <h3>Standard full roof</h3>
        <p>
          Destination Roofing generally provides a 15-year workmanship warranty on a standard full-roof installation.
        </p>
        <h3>Qualifying Atlas Signature Select system</h3>
        <p>A qualifying Atlas Signature Select roof may receive a 20-year Destination Roofing workmanship warranty.</p>
        <h3>Repair work</h3>
        <p>
          Most repairs generally receive a five-year workmanship warranty on the area Destination Roofing directly
          repairs.
        </p>
        <p>
          The workmanship warranty generally covers labor and material required to correct leaks caused by Destination
          Roofing's improper installation, subject to the written contract, exclusions, and warranty terms. It does not
          mean that every future leak from any cause is covered.
        </p>
        <h3>Atlas manufacturer warranty</h3>
        <p>
          Qualifying Atlas products and system components may be eligible for enhanced manufacturer warranty coverage.
          Exact terms, registration requirements, exclusions, transferability, and coverage depend on current official
          Atlas documents and the installed system. The manufacturer warranty is not automatically all-inclusive.
        </p>
        <p>
          Destination Roofing believes a written warranty should mean something. If a legitimate installation-related
          problem develops, the company's approach is to inspect the issue and honor its written responsibility rather
          than automatically search for a reason to avoid the claim.
        </p>

        <h2>Shingle Roofing for Citrus County Conditions</h2>
        <ul>
          <li>Strong Florida sun can accelerate surface wear.</li>
          <li>Heat can expose weakness in low-quality shingles.</li>
          <li>Heavy rainfall tests valleys, flashing, penetrations, and roof transitions.</li>
          <li>Wind can lift, crease, or loosen shingles without completely removing them.</li>
          <li>Ridge caps require proper fastening.</li>
          <li>Humidity makes roof-deck condition and attic ventilation important.</li>
          <li>Tree coverage can contribute to debris, moisture, and surface staining.</li>
          <li>
            Low-slope additions require roofing systems appropriate for their pitch — see{" "}
            <Link to="/flat-roofing">flat roofing</Link>.
          </li>
          <li>
            Soffit intake and roof ventilation should work together — see <Link to="/soffit">soffit and fascia</Link>.
          </li>
          <li>
            Roof-to-wall connections may be evaluated for{" "}
            <Link to="/third-nail-wind-mitigation">third-nail improvements</Link> during reroofing.
          </li>
        </ul>
        <p>
          After a storm, see <Link to="/storm-damage-roof-repair">storm-damage roof repair</Link>. We serve homeowners
          in <Link to="/roofing-company-beverly-hills-fl">Beverly Hills</Link>,{" "}
          <Link to="/roofing-company-inverness-fl">Inverness</Link>,{" "}
          <Link to="/roofing-company-crystal-river-fl">Crystal River</Link>,{" "}
          <Link to="/roofing-company-homosassa-fl">Homosassa</Link>,{" "}
          <Link to="/roofing-company-lecanto-fl">Lecanto</Link>,{" "}
          <Link to="/roofing-company-hernando-fl">Hernando</Link>,{" "}
          <Link to="/roofing-company-floral-city-fl">Floral City</Link>, and{" "}
          <Link to="/roofing-company-citrus-springs-fl">Citrus Springs</Link> — see all{" "}
          <Link to="/service-areas">service areas</Link> and <Link to="/services">roofing services</Link>. For a
          side-by-side overview, read{" "}
          <Link to="/blog/shingle-metal-flat-roofing-citrus-county">
            shingle, metal, and flat roofing in Citrus County
          </Link>
          .
        </p>

        <h2>What Homeowners Say About Destination Roofing</h2>
        <div className="not-prose grid gap-4 md:grid-cols-3 my-6">
          {REVIEWS.map((r) => (
            <blockquote key={r.name} className="border border-border rounded-lg p-5 bg-card">
              <p className="text-sm leading-relaxed">"{r.text}"</p>
              <footer className="mt-3 text-sm font-semibold">— {r.name}</footer>
            </blockquote>
          ))}
        </div>
        <p>
          <Link to="/reviews">Read More Customer Reviews</Link>
        </p>

        <h2>Shingle-Roofing Guidance From an Experienced Local Roofer</h2>
        <p>
          This page is reviewed by Deston Judy, owner and licensed roofing contractor for Destination Roofing Inc.
          Deston began working in roofing at age 15 and personally performs many inspections, explains material and
          repair options, reviews estimates, and checks completed roofing work.
        </p>
        <ul>
          <li>Florida roofing license # {BUSINESS.licenseNumber}</li>
          <li>More than 100 roofs completed by Destination Roofing</li>
          <li>Atlas Scotchgard certification</li>
          <li>Approximately 15 years rooted in Citrus County</li>
        </ul>
        <p>
          <Link to="/about">More about Destination Roofing Inc.</Link>
        </p>

        <h2>Shingle-Roofing Questions</h2>
        <div className="not-prose space-y-3 my-6">
          {FAQS.map((f) => (
            <details key={f.q} className="border border-border rounded-lg bg-card">
              <summary className="cursor-pointer font-semibold px-4 py-3">{f.q}</summary>
              <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>

        <h2 id="lead-form">Request Your Free Shingle-Roof Inspection</h2>
        <p>
          Tell us what you have noticed about your roof and whether you are considering repair or replacement.
          Destination Roofing will contact you to schedule a free inspection. You can also{" "}
          <Link to="/contact">contact us here</Link>.
        </p>

        <h2>Get an Honest Shingle-Roofing Recommendation</h2>
        <p>
          Destination Roofing will inspect the actual roof and explain whether repair, standard replacement, or a
          qualifying Atlas roofing system makes the most sense.
        </p>
        <div className="not-prose flex flex-col sm:flex-row gap-3 my-6">
          <Link to="/contact" className="btn-cta w-full sm:w-auto">
            <CalendarCheck className="size-5" /> Schedule My Free Inspection
          </Link>
          <a
            href={BUSINESS.phoneHref}
            className="inline-flex items-center justify-center gap-2 border-2 border-brand text-brand font-display font-bold uppercase tracking-wider rounded-md px-6 py-3 w-full sm:w-auto hover:bg-brand hover:text-brand-foreground transition-colors"
          >
            <Phone className="size-5" /> Call {BUSINESS.phone}
          </a>
        </div>
      </ServicePageBody>
    </>
  );
}
